// src/utils/corporateTaxCalculations.js
// Calculs fiscaux pour les sociétés (IS)

import {
  IS_RATES_BY_YEAR,
  DEFAULT_HYPOTHESES,
  MOTHER_DAUGHTER_REGIME,
  getISRatesForYear
} from './entityConstants';

/**
 * Calcule l'IS (Impôt sur les Sociétés) pour un résultat fiscal donné
 * @param {number} taxableProfit - Résultat fiscal (bénéfice imposable)
 * @param {number} year - Année fiscale
 * @param {boolean} isPME - Éligible au taux réduit PME (défaut: true)
 * @returns {object} - { is: montant IS, details: { reducedPart, normalPart } }
 */
export const calculateCorporateTax = (taxableProfit, year = 2024, isPME = true) => {
  if (taxableProfit <= 0) {
    return { is: 0, details: { reducedPart: 0, normalPart: 0 } };
  }

  const rates = getISRatesForYear(year);
  const { reducedRateLimit, reducedRate, normalRate } = rates;

  let reducedPart = 0;
  let normalPart = 0;

  if (isPME && taxableProfit <= reducedRateLimit) {
    // Tout au taux réduit
    reducedPart = taxableProfit * reducedRate;
  } else if (isPME) {
    // Taux réduit jusqu'au seuil, puis taux normal
    reducedPart = reducedRateLimit * reducedRate;
    normalPart = (taxableProfit - reducedRateLimit) * normalRate;
  } else {
    // Pas de taux réduit (grande entreprise)
    normalPart = taxableProfit * normalRate;
  }

  return {
    is: Math.round((reducedPart + normalPart) * 100) / 100,
    details: {
      reducedPart: Math.round(reducedPart * 100) / 100,
      normalPart: Math.round(normalPart * 100) / 100,
      reducedRateLimit,
      reducedRate,
      normalRate
    }
  };
};

/**
 * Calcule les charges basées sur les hypothèses (% du CA ou fixe)
 * @param {number} ca - Chiffre d'affaires
 * @param {object} hypotheses - Hypothèses de calcul
 * @param {object} manualOverrides - Valeurs manuelles (override)
 * @returns {object} - Charges détaillées
 */
export const calculateCharges = (ca, hypotheses = {}, manualOverrides = {}) => {
  const h = { ...DEFAULT_HYPOTHESES, ...hypotheses };

  // Personnel : % du CA ou valeur manuelle
  const personnel = manualOverrides.personnel !== null && manualOverrides.personnel !== undefined
    ? manualOverrides.personnel
    : ca * (h.personnelPercent / 100);

  // Achats : % du CA ou valeur manuelle
  const achats = manualOverrides.achats !== null && manualOverrides.achats !== undefined
    ? manualOverrides.achats
    : ca * (h.achatsPercent / 100);

  // Loyers : montant fixe ou valeur manuelle
  const loyers = manualOverrides.loyers !== null && manualOverrides.loyers !== undefined
    ? manualOverrides.loyers
    : (h.loyersFixed || 0);

  // Amortissements : % du CA ou valeur manuelle
  const amortissements = manualOverrides.amortissements !== null && manualOverrides.amortissements !== undefined
    ? manualOverrides.amortissements
    : ca * (h.amortPercent / 100);

  // Autres charges : valeur manuelle uniquement
  const autres = manualOverrides.autresCharges || 0;

  const total = personnel + achats + loyers + amortissements + autres;

  return {
    personnel: Math.round(personnel * 100) / 100,
    achats: Math.round(achats * 100) / 100,
    loyers: Math.round(loyers * 100) / 100,
    amortissements: Math.round(amortissements * 100) / 100,
    autres: Math.round(autres * 100) / 100,
    total: Math.round(total * 100) / 100,
    // Indicateurs de source (hypothèse ou manuel)
    sources: {
      personnel: manualOverrides.personnel !== null && manualOverrides.personnel !== undefined ? 'manual' : 'hypothesis',
      achats: manualOverrides.achats !== null && manualOverrides.achats !== undefined ? 'manual' : 'hypothesis',
      loyers: manualOverrides.loyers !== null && manualOverrides.loyers !== undefined ? 'manual' : 'hypothesis',
      amortissements: manualOverrides.amortissements !== null && manualOverrides.amortissements !== undefined ? 'manual' : 'hypothesis',
      autres: 'manual'
    }
  };
};

/**
 * Calcule le résultat complet d'un exercice
 * @param {object} params - Paramètres de calcul
 * @returns {object} - Résultat complet de l'exercice
 */
export const calculateExerciceResult = ({
  ca,                        // Chiffre d'affaires
  autresProduits = 0,        // Autres produits
  dividendesRecus = 0,       // Dividendes reçus de filiales
  isMotherDaughter = false,  // Régime mère-fille applicable
  hypotheses = {},           // Hypothèses de calcul
  manualOverrides = {},      // Valeurs manuelles
  year = 2024,              // Année fiscale
  isPME = true,             // Éligible taux réduit
  taxRegime = 'IS',         // IS ou IR
  dividendsToDistribute = null,  // Dividendes à distribuer (null = hypothèse)
  reportNMoins1 = 0         // Report à nouveau N-1
}) => {
  // 1. Calcul des produits
  let dividendesImposables = dividendesRecus;
  let dividendesExoneres = 0;

  if (isMotherDaughter && dividendesRecus > 0) {
    // Régime mère-fille : 95% exonéré, 5% imposable (quote-part frais et charges)
    dividendesExoneres = dividendesRecus * (MOTHER_DAUGHTER_REGIME.exemptionRate / 100);
    dividendesImposables = dividendesRecus * (MOTHER_DAUGHTER_REGIME.taxableQuoteShare / 100);
  }

  const totalProduits = ca + autresProduits + dividendesImposables;

  // 2. Calcul des charges
  const charges = calculateCharges(ca, hypotheses, manualOverrides);

  // 3. Résultat brut (avant impôt)
  const resultatBrut = totalProduits - charges.total;

  // 4. Calcul IS (si régime IS et bénéfice)
  let isResult = { is: 0, details: {} };
  if (taxRegime === 'IS' && resultatBrut > 0) {
    isResult = calculateCorporateTax(resultatBrut, year, isPME);
  }

  // 5. Résultat net
  const resultatNet = resultatBrut - isResult.is;

  // 6. Affectation du résultat
  const h = { ...DEFAULT_HYPOTHESES, ...hypotheses };
  const dividendes = dividendsToDistribute !== null && dividendsToDistribute !== undefined
    ? dividendsToDistribute
    : Math.max(0, resultatNet * (h.dividendPercent / 100));

  const resultatDisponible = resultatNet + reportNMoins1;
  const reportNNouveau = resultatDisponible - dividendes;

  return {
    // Produits
    produits: {
      ca,
      autresProduits,
      dividendesRecus,
      dividendesImposables: Math.round(dividendesImposables * 100) / 100,
      dividendesExoneres: Math.round(dividendesExoneres * 100) / 100,
      total: Math.round(totalProduits * 100) / 100
    },
    // Charges
    charges,
    // Résultats
    resultatBrut: Math.round(resultatBrut * 100) / 100,
    is: isResult.is,
    isDetails: isResult.details,
    resultatNet: Math.round(resultatNet * 100) / 100,
    // Affectation
    affectation: {
      reportNMoins1,
      resultatDisponible: Math.round(resultatDisponible * 100) / 100,
      dividendes: Math.round(dividendes * 100) / 100,
      dividendesSource: dividendsToDistribute !== null ? 'manual' : 'hypothesis',
      reportNNouveau: Math.round(reportNNouveau * 100) / 100
    },
    // Métadonnées
    meta: {
      year,
      taxRegime,
      isPME,
      isMotherDaughter
    }
  };
};

/**
 * Calcule la répartition des dividendes aux associés
 * @param {number} totalDividendes - Montant total des dividendes
 * @param {Array} shareholdings - Liste des participations { ownerId, ownerType, percentage }
 * @returns {Array} - Répartition par associé
 */
export const calculateDividendDistribution = (totalDividendes, shareholdings = []) => {
  if (!shareholdings.length || totalDividendes <= 0) {
    return [];
  }

  return shareholdings.map(sh => {
    const brut = totalDividendes * (sh.percentage / 100);

    // Pour les personnes physiques : PFU 30% ou barème
    // Pour les sociétés : régime mère-fille potentiel
    let impot = 0;
    let net = brut;

    if (sh.ownerType === 'individual') {
      // PFU par défaut (30%)
      const pfuRate = sh.dividendTaxOption === 'BAREME' ? 0 : 0.30;
      impot = brut * pfuRate;
      net = brut - impot;
    } else if (sh.ownerType === 'entity') {
      // Société : potentiellement régime mère-fille
      // À affiner selon les cas
      if (sh.percentage >= MOTHER_DAUGHTER_REGIME.minimumHolding) {
        // Régime mère-fille potentiel
        impot = brut * 0.05 * 0.25; // 5% × taux IS
        net = brut - impot;
      }
    }

    return {
      ownerId: sh.ownerId,
      ownerType: sh.ownerType,
      ownerName: sh.ownerName,
      percentage: sh.percentage,
      brut: Math.round(brut * 100) / 100,
      impot: Math.round(impot * 100) / 100,
      net: Math.round(net * 100) / 100,
      taxOption: sh.dividendTaxOption || 'PFU'
    };
  });
};

/**
 * Agrège les résultats de plusieurs sources de CA pour un exercice
 * @param {Array} sources - Sources avec leurs yearly amounts
 * @param {number} year - Année à agréger
 * @param {object} entityDefaults - Hypothèses par défaut de l'entité
 * @returns {object} - Résultat agrégé
 */
export const aggregateSourcesForYear = (sources, year, entityDefaults = {}) => {
  let totalCA = 0;
  let totalAutresProduits = 0;
  let totalDividendesRecus = 0;
  let hasMotherDaughter = false;

  // Parcourir les sources
  sources.forEach(source => {
    const yearData = source.yearly_amounts?.find(ya => ya.year === year);
    if (!yearData) return;

    switch (source.source_type) {
      case 'CA_PRINCIPAL':
      case 'CA_SECONDAIRE':
        totalCA += yearData.amount || 0;
        break;
      case 'DIVIDENDES_RECUS':
        totalDividendesRecus += yearData.amount || 0;
        if (source.is_mother_daughter) hasMotherDaughter = true;
        break;
      case 'AUTRES_PRODUITS':
        totalAutresProduits += yearData.amount || 0;
        break;
    }
  });

  return {
    ca: totalCA,
    autresProduits: totalAutresProduits,
    dividendesRecus: totalDividendesRecus,
    hasMotherDaughter,
    year
  };
};
